import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileDemo } from '../hooks/useFileDemo';
import type { FileUploadResponse } from '../types/showcase';

const TEN_MB = 10 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function FilePanel() {
  const { t } = useTranslation();
  const { mutate, isPending, data, isSuccess, isError, error, reset } = useFileDemo();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showLargeConfirm, setShowLargeConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  }

  function doUpload(file: File) {
    setShowLargeConfirm(false);
    mutate(file);
  }

  function handleUpload() {
    if (!selectedFile) return;
    if (selectedFile.size > TEN_MB) {
      setShowLargeConfirm(true);
    } else {
      doUpload(selectedFile);
    }
  }

  function handleDialogClose() {
    setShowLargeConfirm(false);
  }

  function handleDialogConfirm() {
    if (selectedFile) doUpload(selectedFile);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      handleDialogClose();
    }
  }

  return (
    <section aria-labelledby="file-panel-title">
      <h2
        id="file-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.file.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        C009 — FileService.upload() · category=showcase-trial
      </p>

      {/* File input */}
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label
          htmlFor="file-input"
          style={{
            display: 'block',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-2)',
          }}
        >
          {t('showcase.file.select')}
        </label>
        <button
          type="button"
          style={{
            border: '2px dashed var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-6)',
            textAlign: 'center',
            cursor: 'pointer',
            background: selectedFile ? 'var(--color-sidebar-active-bg)' : 'transparent',
            width: '100%',
            fontFamily: 'var(--font-family-sans)',
          }}
          onClick={() => fileInputRef.current?.click()}
          aria-label={t('showcase.file.selectArea')}
        >
          <input
            ref={fileInputRef}
            id="file-input"
            type="file"
            aria-label={t('showcase.file.select')}
            onChange={handleFileChange}
            disabled={isPending}
            style={{ display: 'none' }}
          />
          {selectedFile ? (
            <div>
              <p style={{ color: 'var(--color-brand-primary)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
                {selectedFile.name}
              </p>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                {formatBytes(selectedFile.size)} · {selectedFile.type || 'unknown'}
              </p>
            </div>
          ) : (
            <div>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>
                {t('showcase.file.selectClick')}
              </p>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                {t('showcase.file.selectHint')}
              </p>
            </div>
          )}
        </button>
      </div>

      {/* Upload button */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
        <button
          type="button"
          disabled={isPending || !selectedFile}
          aria-busy={isPending}
          onClick={handleUpload}
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            background:
              isPending || !selectedFile
                ? 'var(--color-text-muted)'
                : 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: isPending || !selectedFile ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {isPending && (
            <span
              aria-hidden="true"
              style={{
                width: 14,
                height: 14,
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: 'var(--color-text-inverse)',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin 0.8s linear infinite',
              }}
            />
          )}
          {isPending ? t('showcase.file.loading') : t('showcase.file.upload')}
        </button>
        {(isSuccess || isError) && (
          <button
            type="button"
            onClick={() => {
              reset();
              setSelectedFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('showcase.common.reset')}
          </button>
        )}
      </div>

      {/* Result card */}
      {isSuccess && data && (
        <div
          role="region"
          aria-label={t('showcase.file.resultLabel')}
          style={{
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)' }}>
            <span style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-lg)' }} aria-hidden="true">✓</span>
            <strong style={{ color: 'var(--color-success)' }}>{t('showcase.file.uploadComplete')}</strong>
          </div>

          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.file.result.fileId')}</dt>
            <dd style={{ margin: 0 }}>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', wordBreak: 'break-all' }}>
                {data.fileId}
              </code>
            </dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.file.result.filename')}</dt>
            <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>{data.originalName}</dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.file.result.size')}</dt>
            <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>{formatBytes(data.size)}</dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.file.result.mimeType')}</dt>
            <dd style={{ margin: 0 }}>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {data.mimeType}
              </code>
            </dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.file.result.expiresAt')}</dt>
            <dd style={{ margin: 0, color: 'var(--color-warning)', fontSize: 'var(--font-size-xs)' }}>
              {data.expiresAt}
            </dd>
          </dl>

          {/* E: blob download fix */}
          <DownloadButton fileData={data} />
        </div>
      )}

      {/* Error */}
      {isError && error && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <strong style={{ color: 'var(--color-danger)' }}>{error.code}</strong>
          <p style={{ color: 'var(--color-text-primary)', margin: 'var(--spacing-2) 0 0' }}>{error.message}</p>
        </div>
      )}

      {/* Large file confirm dialog */}
      {showLargeConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="large-file-dialog-title"
          aria-describedby="large-file-dialog-desc"
          onKeyDown={handleKeyDown}
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            background: 'var(--color-overlay-backdrop)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              maxWidth: 420,
              width: '90%',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <h3
              id="large-file-dialog-title"
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginTop: 0,
                marginBottom: 'var(--spacing-3)',
              }}
            >
              {t('showcase.file.confirmLargeFile.title')}
            </h3>
            <p
              id="large-file-dialog-desc"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-6)',
              }}
            >
              {t('showcase.file.confirmLargeFile.message')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleDialogClose}
                autoFocus
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('showcase.file.confirmLargeFile.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDialogConfirm}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('showcase.file.confirmLargeFile.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning message */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.file.learning')}
      </div>
    </section>
  );
}

// E: blob download button using fetch → createObjectURL
function DownloadButton({ fileData }: { fileData: FileUploadResponse }) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState<string | null>(null);

  async function handleDownload() {
    setDownloading(true);
    setDlError(null);
    try {
      const response = await fetch(fileData.presignedDownloadUrl, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? sessionStorage.getItem('access_token') ?? ''}` },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileData.originalName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDlError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div style={{ marginTop: 'var(--spacing-3)' }}>
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={downloading}
        aria-busy={downloading}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          background: downloading ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
          color: 'var(--color-text-inverse)',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          cursor: downloading ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        {t('showcase.file.result.download')}
      </button>
      {dlError && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--spacing-1)' }}>
          {dlError}
        </p>
      )}
    </div>
  );
}
